import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import axiosInstance from "../../utils/axiosInstance";

/**
 * M2: the active squad an action leaves behind when it settles discovery, or
 * `undefined` for any other action. Read by action type so this slice does not
 * import the user slice (several suites mock it). The same function sits in
 * store/tasks/slice.js and store/planTasks/slice.js; the squad itself is
 * decided once, by the user slice, and carried as `settledSquadId`.
 */
const settledActiveSquadOf = (action) => {
  // A superseded discovery reply settles nothing: the user slice ignores it.
  if (action.payload?.superseded === true) return undefined;
  switch (action.type) {
    case "user/fetchExperienceContexts/fulfilled":
      return action.payload?.settledSquadId ?? null;
    case "user/fetchExperienceContexts/rejected":
      return action.payload?.status === 409 ? null : undefined;
    case "user/resetExperienceContext":
      return null;
    default:
      return undefined;
  }
};

/* The marker a request id takes when a context change voids it. */
const CONTEXT_CHANGED = "context_changed";

export const fetchSquadUsersAndQuests = createAsyncThunk(
  "tasks/fetchSquadUsersAndQuests",
  async (squadId) => {
    const response = await axiosInstance(
      `/squad-users/?include=user.quests&squad_id=${squadId}`,
      "get",
    );
    return response.data.data;
  },
);

export const fetchSquadUsers = createAsyncThunk(
  "tasks/fetchSquadUsers",
  async (squadId) => {
    const response = await axiosInstance(
      `/squad-users/?include=user&squad_id=${squadId}`,
      "get",
    );
    return response.data.data;
  },
);

// Create a new quest
export const createQuest = createAsyncThunk(
  "tasks/createQuest",
  async (questData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance("/quests", "post", questData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to create quest");
    }
  },
);

// Update a quest
export const updateTask = createAsyncThunk(
  "tasks/updateTask",
  async (updateData, { rejectWithValue }) => {
    try {
      const { id, ...updateTaskData } = updateData;
      const response = await axiosInstance(
        `/quests/${id}`,
        "put",
        updateTaskData,
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to update quest");
    }
  },
);

export const updateTaskStatus = createAsyncThunk(
  "tasks/updateTaskStatus",
  async (data, { signal }) => {
    const { id, status_id } = data;
    const response = await axiosInstance(
      `/quests/${id}`,
      "put",
      {
        status_id: status_id,
      },
      { signal },
    );
    return response.data.data;
  },
);

// Delete a quest
//
// Rejects with the RAW response body, matching `updateTask` and `createQuest`
// above. It previously reduced every rejection to `data.message`, a bare
// string, which threw away the rest of the body and left no way for a caller
// to tell one refusal from another.
export const deleteTask = createAsyncThunk(
  "tasks/deleteTask",
  async (taskId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance(`/quests/${taskId}`, "delete");
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to delete task");
    }
  },
);

// Release a quest (unassign user)
//
// Same raw-body contract as its three siblings. Consistency here is the point:
// the one thunk in this file that reduced rejections to a string is the one
// whose caller stopped checking them at all.
export const releaseTask = createAsyncThunk(
  "tasks/releaseTask",
  async (taskId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance(`/quests/${taskId}/release`, "post");
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to release task");
    }
  },
);

const initialState = {
  squadUsers: [],
  squadOnlyUsers: [],
  unassignedTasks: [],
  loading: false,
  error: null,
  // M2: the squad the held roster was read for, the squad of the newest
  // roster request, and the newest request ids. A context change clears the
  // roster and voids every request in flight (CONTEXT_CHANGED), so a reply
  // for squad A can never land over squad B.
  rosterSquadId: null,
  rosterRequestSquadId: null,
  latestRosterRequestId: null,
  latestSquadUsersRequestId: null,
};

const squadIdOf = (value) => {
  const n = Number(value);
  return value !== null &&
    value !== undefined &&
    Number.isSafeInteger(n) &&
    n > 0
    ? n
    : null;
};

/* A reply is current when no request is recorded (a hand-built store) or it
   is the newest one. CONTEXT_CHANGED matches no reply. */
const isLatest = (latest, action) =>
  latest === null || action.meta?.requestId === latest;

const taskSlice = createSlice({
  name: "tasks",
  initialState,
  reducers: {
    updateSquadUserPoint: (state, action) => {
      /**
       * W4-F1 GUARD: A NULL PERSONAL TOTAL IS "THIS TRANSPORT CANNOT ANSWER FOR
       * A PERSON", NOT "THIS PERSON HAS NONE".
       *
       * The Wave 4 economy message is broadcast to every member of a squad and
       * therefore carries `user_id: null` and `user_points: null` BY CONTRACT,
       * for every event including a personal one (technical brief 4.4). The
       * line below reads an absolute total as `Number(value) || 0`, so without
       * this the payload designed to withhold a number would have been read as
       * a zero, and every member of the squad would have lost their XP display
       * at the same instant, once per teammate's action.
       *
       * `WebSocketContext` routes economy messages away from this action, which
       * is why the guard belongs HERE as well: the routing is one `if` in a
       * component and the contract is a property of the payload.
       */
      const incoming = action.payload?.user_points;
      if (incoming === null || incoming === undefined) return;
      if (!Number.isFinite(Number(incoming))) return;
      const index = state.squadUsers.findIndex(
        (su) =>
          su.user_id === action.payload.user_id &&
          su.squad_id === action.payload.squad_id,
      );
      if (index !== -1) {
        const newPoints = Number(action.payload.user_points) || 0;
        const currentPoints = state.squadUsers[index].points || 0;
        // Sanity check: reject absurdly large values (> 1M absolute cap)
        if (newPoints > 1000000) {
          console.warn(
            "[XP] Rejected suspicious user_points value:",
            newPoints,
            "current:",
            currentPoints,
          );
          return;
        }
        state.squadUsers[index].points = newPoints;
      }
    },
    /**
     * The v6 Plan board's lock creates the quest on the server (finalize),
     * not through createQuest; this puts it in its owner's list the same way
     * createQuest.fulfilled does, so the journey sees the pending task at
     * once (slice V sandbox D2). No duplicate if the list already has it.
     */
    addOwnQuest(state, action) {
      const quest = action.payload;
      const index = state.squadUsers.findIndex(
        (su) => su.user?.id === quest?.user_id,
      );
      if (index === -1) return;
      const quests = state.squadUsers[index].user.quests || [];
      if (quests.some((q) => Number(q.id) === Number(quest.id))) return;
      state.squadUsers[index].user.quests = [...quests, quest];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(createQuest.fulfilled, (state, action) => {
      // M2: a quest written for another squad (the context moved while the
      // write was in flight) never lands in this squad's roster.
      const questSquad = squadIdOf(
        action.payload?.squad_id ?? action.meta?.arg?.squad_id,
      );
      if (
        state.rosterSquadId !== null &&
        questSquad !== null &&
        questSquad !== state.rosterSquadId
      ) {
        return;
      }
      const index = state.squadUsers.findIndex(
        (su) => su.user.id === action.payload.user_id,
      );
      if (index !== -1) {
        state.squadUsers[index].user.quests.push(action.payload);
      }
    });
    builder.addCase(updateTask.fulfilled, (state, action) => {
      // Handle task reassignment: update across all squad users
      // First, remove the task from any user who currently has it
      state.squadUsers.forEach((squadUser) => {
        squadUser.user.quests = squadUser.user.quests.filter(
          (task) => task.id !== action.payload.id,
        );
      });

      // Also remove from unassigned tasks if it was there
      state.unassignedTasks = state.unassignedTasks.filter(
        (task) => task.id !== action.payload.id,
      );

      // Then add the updated task to the appropriate place
      if (action.payload.user_id) {
        // Task is assigned - add to the owner's quests
        const newOwnerIndex = state.squadUsers.findIndex(
          (su) => su.user.id === action.payload.user_id,
        );
        if (newOwnerIndex !== -1) {
          state.squadUsers[newOwnerIndex].user.quests.push(action.payload);
        }
      } else {
        // Task is unassigned (released) - add to unassignedTasks
        state.unassignedTasks.push(action.payload);
      }
    });
    builder.addCase(updateTaskStatus.fulfilled, (state, action) => {
      state.squadUsers.forEach((squadUser) => {
        squadUser.user.quests = squadUser.user.quests.map((task) =>
          task.id === action.payload.id ? action.payload : task,
        );
      });
    });
    builder.addCase(fetchSquadUsers.pending, (state, action) => {
      state.latestSquadUsersRequestId = action.meta?.requestId ?? null;
    });
    builder.addCase(fetchSquadUsers.fulfilled, (state, action) => {
      if (!isLatest(state.latestSquadUsersRequestId, action)) return;
      const uniqueUsers = new Map();
      action.payload.forEach((user) => {
        if (user.user && !uniqueUsers.has(user.user.id)) {
          uniqueUsers.set(user.user.id, user.user);
        }
      });
      state.squadOnlyUsers = Array.from(uniqueUsers.values());
    });
    builder.addCase(fetchSquadUsersAndQuests.pending, (state, action) => {
      state.latestRosterRequestId = action.meta?.requestId ?? null;
      state.rosterRequestSquadId = squadIdOf(action.meta?.arg);
    });
    builder.addCase(fetchSquadUsersAndQuests.fulfilled, (state, action) => {
      if (!isLatest(state.latestRosterRequestId, action)) return;
      state.squadUsers = action.payload;
      state.rosterSquadId = squadIdOf(action.meta?.arg);
    });
    builder.addCase(deleteTask.fulfilled, (state, action) => {
      state.squadUsers.forEach((squadUser) => {
        squadUser.user.quests = squadUser.user.quests.filter(
          (task) => task.id.toString() !== action.payload.toString(),
        );
      });
    });
    builder.addCase(releaseTask.fulfilled, (state, action) => {
      // Remove task from all squad users' quests
      state.squadUsers.forEach((squadUser) => {
        squadUser.user.quests = squadUser.user.quests.filter(
          (task) => task.id !== action.payload.id,
        );
      });
      // Add to unassigned tasks
      state.unassignedTasks.push(action.payload);
    });
    // M2: the active context moved to another squad (or to none). The held
    // roster belongs to the previous one: clear it and void its requests.
    builder.addMatcher(
      (action) => settledActiveSquadOf(action) !== undefined,
      (state, action) => {
        const next = settledActiveSquadOf(action);
        const held = state.rosterSquadId ?? state.rosterRequestSquadId;
        if (held === null || held === next) return;
        state.squadUsers = [];
        state.squadOnlyUsers = [];
        state.unassignedTasks = [];
        state.rosterSquadId = null;
        state.rosterRequestSquadId = null;
        state.latestRosterRequestId = CONTEXT_CHANGED;
        state.latestSquadUsersRequestId = CONTEXT_CHANGED;
      },
    );
  },
});

export const { updateSquadUserPoint, addOwnQuest } = taskSlice.actions;

export default taskSlice.reducer;
